// Meter detail card for SLD detail panel — Gamma design system
import { StatusBadge } from './shared/StatusBadge';
import { DeviceConditionSections } from './shared/DeviceConditionSections';
import { formatVoltage } from '../../../utils/formatVoltage';

interface MeterDetailCardProps {
  siteId?: string;
  data: Record<string, unknown>;
  onViewDetails: () => void;
}

function num(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return isFinite(n) ? n : null;
}

function fmt(v: unknown, decimals = 1): string {
  if (v == null) return '--';
  const n = Number(v);
  if (!isFinite(n)) return '--';
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtFreq(v: unknown): string {
  if (v == null) return '--';
  const n = Number(v);
  if (!isFinite(n)) return '--';
  return n.toFixed(3);
}

function getDeviationColor(mHz: number): string {
  const abs = Math.abs(mHz);
  if (abs < 100) return 'text-ems-green';
  if (abs < 500) return 'text-ems-amber';
  return 'text-ems-red';
}

export function MeterDetailCard({ siteId, data, onViewDetails }: MeterDetailCardProps) {
  const label = String(data.label ?? 'Site Meter');
  const deviceId = data.deviceId as string | undefined;
  const status = String(data.status ?? 'active').toLowerCase();

  const activePower = num(data.activePowerKw);
  const reactivePower = num(data.reactivePowerKvar ?? data.reactivePower);
  const powerFactor = num(data.powerFactor);
  const frequency = num(data.frequencyHz);
  const voltageA = num(data.voltagePhaseA);
  const voltageB = num(data.voltagePhaseB);
  const voltageC = num(data.voltagePhaseC);
  const currentA = num(data.currentPhaseA);
  const currentB = num(data.currentPhaseB);
  const currentC = num(data.currentPhaseC);
  const apparentPower = num(data.apparentPower);

  // Frequency deviation from 60Hz nominal
  const deviationMHz = frequency != null ? (frequency - 60.0) * 1000 : null;
  const deviationColor = deviationMHz != null ? getDeviationColor(deviationMHz) : 'text-ems-text-muted';
  const freqStable = deviationMHz != null && Math.abs(deviationMHz) < 500;

  // Use per-phase voltages if available, otherwise fall back to main voltage
  const mainVoltage = num(data.voltageV);
  const hasPhaseVoltages = voltageA != null || voltageB != null || voltageC != null;
  const hasPhaseCurrents = currentA != null || currentB != null || currentC != null;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-ems-border flex-shrink-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${status === 'active' || status === 'online' ? 'bg-ems-green' : 'bg-ems-text-muted'}`} />
          <h3 className="text-lg font-semibold text-ems-text-primary truncate">{label}</h3>
          {deviceId && (
            <span className="text-[10px] bg-ems-bg px-1.5 py-0.5 rounded font-mono text-ems-text-muted flex-shrink-0">
              {deviceId}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 ml-4">
          <StatusBadge status={status} />
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {/* Grid Frequency — Primary Metric */}
        <div>
          <span className="text-xs uppercase tracking-wider text-ems-text-secondary mb-1 block">Grid Frequency</span>
          <div className="text-3xl font-mono font-bold text-ems-green transition-all duration-300">
            {fmtFreq(frequency)} <span className="text-sm font-normal text-ems-text-muted">Hz</span>
          </div>
          {deviationMHz != null && (
            <div className={`text-xs font-mono mt-0.5 ${deviationColor} transition-all duration-300`}>
              Deviation: {deviationMHz >= 0 ? '+' : ''}{deviationMHz.toFixed(0)} mHz
            </div>
          )}
        </div>

        {/* Power Row — 3 columns */}
        <div className="rounded-xl bg-ems-bg p-3">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <span className="text-[10px] text-ems-text-muted uppercase tracking-wide block">Active</span>
              <span className="text-sm font-mono font-semibold text-ems-text-primary transition-all duration-300">
                {fmt(activePower, 0)}
              </span>
              <span className="text-[10px] text-ems-text-muted ml-0.5">kW</span>
            </div>
            <div>
              <span className="text-[10px] text-ems-text-muted uppercase tracking-wide block">Reactive</span>
              <span className="text-sm font-mono font-semibold text-ems-text-primary transition-all duration-300">
                {fmt(reactivePower, 0)}
              </span>
              <span className="text-[10px] text-ems-text-muted ml-0.5">kVAR</span>
            </div>
            <div>
              <span className="text-[10px] text-ems-text-muted uppercase tracking-wide block">PF</span>
              <span className="text-sm font-mono font-semibold text-ems-text-primary transition-all duration-300">
                {powerFactor != null ? powerFactor.toFixed(2) : '--'}
              </span>
            </div>
          </div>
        </div>

        {/* Line Voltages */}
        <div className="rounded-xl bg-ems-bg p-3">
          <span className="text-xs uppercase tracking-wider text-ems-text-secondary block mb-2">Line Voltages</span>
          {hasPhaseVoltages ? (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              <div className="flex justify-between">
                <span className="text-[11px] text-ems-text-muted">L1:</span>
                <span className="text-[11px] font-mono text-ems-text-primary transition-all duration-300">{formatVoltage(voltageA, 'ac')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[11px] text-ems-text-muted">L2:</span>
                <span className="text-[11px] font-mono text-ems-text-primary transition-all duration-300">{formatVoltage(voltageB, 'ac')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[11px] text-ems-text-muted">L3:</span>
                <span className="text-[11px] font-mono text-ems-text-primary transition-all duration-300">{formatVoltage(voltageC, 'ac')}</span>
              </div>
            </div>
          ) : (
            <div className="flex justify-between">
              <span className="text-[11px] text-ems-text-muted">Avg:</span>
              <span className="text-[11px] font-mono text-ems-text-primary transition-all duration-300">{formatVoltage(mainVoltage, 'ac')}</span>
            </div>
          )}
        </div>

        {/* Line Currents */}
        <div className="rounded-xl bg-ems-bg p-3">
          <span className="text-xs uppercase tracking-wider text-ems-text-secondary block mb-2">Line Currents (A)</span>
          {hasPhaseCurrents ? (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              <div className="flex justify-between">
                <span className="text-[11px] text-ems-text-muted">L1:</span>
                <span className="text-[11px] font-mono text-ems-text-primary transition-all duration-300">{fmt(currentA)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[11px] text-ems-text-muted">L2:</span>
                <span className="text-[11px] font-mono text-ems-text-primary transition-all duration-300">{fmt(currentB)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[11px] text-ems-text-muted">L3:</span>
                <span className="text-[11px] font-mono text-ems-text-primary transition-all duration-300">{fmt(currentC)}</span>
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-ems-text-muted text-center py-1">No phase data</div>
          )}
        </div>

        {/* Apparent Power */}
        {apparentPower != null && (
          <div className="rounded-xl bg-ems-bg p-3">
            <span className="text-xs uppercase tracking-wider text-ems-text-secondary block mb-1">Power Quality</span>
            <div className="flex justify-between">
              <span className="text-[11px] text-ems-text-muted">Apparent Power</span>
              <span className="text-[11px] font-mono text-ems-text-primary transition-all duration-300">{fmt(apparentPower)} kVA</span>
            </div>
          </div>
        )}

        {/* Frequency Stability Indicator */}
        <div className={`rounded-xl p-2.5 text-center text-xs font-medium ${
          frequency == null
            ? 'bg-ems-bg text-ems-text-muted'
            : freqStable
              ? 'bg-ems-green/10 text-ems-green'
              : 'bg-ems-amber/10 text-ems-amber'
        }`}>
          {frequency == null
            ? 'No frequency data'
            : freqStable
              ? '\u2713 Frequency stable'
              : '\u26A0 Frequency deviation detected'
          }
        </div>

        <DeviceConditionSections
          siteId={siteId}
          deviceId={deviceId}
          data={data}
        />
      </div>

      {/* View Details Button */}
      <div className="px-4 py-3 border-t border-ems-border flex-shrink-0">
        <button
          onClick={onViewDetails}
          className="w-full py-2 px-4 bg-ems-forest border border-[rgba(52,211,153,0.2)] rounded-lg text-sm text-ems-green font-medium transition-colors hover:bg-ems-forest/80 hover:border-ems-border-bright"
        >
          View Details &rarr;
        </button>
      </div>
    </div>
  );
}
